import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTrainingResults } from "@/hooks/putting/useTrainingResults";
import PuttingHistoryTabs from "@/components/putting/training/PuttingHistoryTabs";
import MultiTestResultsChart from "@/components/putting/training/MultiTestResultsChart";
import { Button } from "@/components/ui/button";
import NavigationMenu from "@/components/layout/NavigationMenu";


const ChippingTraining = () => {
  const { t } = useTranslation('training');
  // State pour suivre le niveau sélectionné
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  
  // Utilisation du hook spécifique pour les entraînements
  const { 
    trainingResults, 
    isLoading, 
    chartDataByTest,
    testTypes,
    level1Results, 
    level2Results, 
    level3Results 
  } = useTrainingResults();

  // Filtrer uniquement les résultats liés au chipping
  const chippingTestTypes = testTypes.filter(type => 
    type.toLowerCase().includes('chipping') ||
    type.toLowerCase().includes('balle levée') ||
    type.toLowerCase().includes('balle roulée')
  );
  
  const chippingResults = trainingResults.filter(result => {
    const testName = (result.test_name || '').toLowerCase();
    const testId = (result.test_id || '').toLowerCase();
    return testName.includes('chipping') || 
           testId.includes('chipping') ||
           testName.includes('balle levée') ||
           testName.includes('balle roulée');
  });
  
  // Filtrer les résultats de chipping par niveau
  const chippingLevel1Results = level1Results.filter(result => {
    const testName = (result.test_name || '').toLowerCase();
    const testId = (result.test_id || '').toLowerCase();
    return testName.includes('chipping') || 
           testId.includes('chipping') ||
           testName.includes('balle levée') ||
           testName.includes('balle roulée');
  });
  
  const chippingLevel2Results = level2Results.filter(result => {
    const testName = (result.test_name || '').toLowerCase();
    const testId = (result.test_id || '').toLowerCase();
    return testName.includes('chipping') || 
           testId.includes('chipping') ||
           testName.includes('balle levée') ||
           testName.includes('balle roulée');
  });
  
  const chippingLevel3Results = level3Results.filter(result => {
    const testName = (result.test_name || '').toLowerCase();
    const testId = (result.test_id || '').toLowerCase();
    return testName.includes('chipping') || 
           testId.includes('chipping') ||
           testName.includes('balle levée') ||
           testName.includes('balle roulée');
  });

  // Gérer le changement de niveau dans les tabs
  const handleLevelChange = (level: string | null) => {
    setSelectedLevel(level);
  };
  

  // Préparer les données filtrées par type de test pour le graphique
  const filteredChartDataByTest: Record<string, any> = {};
  chippingTestTypes.forEach(testType => {
    if (chartDataByTest[testType]) {
      filteredChartDataByTest[testType] = chartDataByTest[testType];
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      <div className="absolute top-20 left-6">
        <Button 
          variant="ghost" 
          className="text-golf-600 hover:text-golf-700 flex items-center gap-2" 
          asChild
        >
          <Link to="/training">
            <ChevronLeft className="h-5 w-5" />
            {t('chipping.backToTraining')}
          </Link>
        </Button>
      </div>
      <div className="container mx-auto py-8 px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-golf-800 mb-4">{t('chipping.title')}</h1>
          <p className="text-xl text-golf-600">{t('chipping.subtitle')}</p>
        </motion.div>
        
        
        <div className="my-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Results Chart - Now in the left wider column */}
          <div className="lg:col-span-2">
            <MultiTestResultsChart 
              chartDataByTest={filteredChartDataByTest}
              testTypes={chippingTestTypes}
              selectedLevel={selectedLevel}
              trainingResults={chippingResults}
            />
          </div>
          
          {/* Results History - Now in the right narrower column */}
          <div className="lg:col-span-1">
            <PuttingHistoryTabs 
              puttingResults={chippingResults}
              level1Results={chippingLevel1Results}
              level2Results={chippingLevel2Results}
              level3Results={chippingLevel3Results}
              onLevelChange={handleLevelChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChippingTraining;
